import { Stack, Typography } from '@mui/material';
import { useUserData } from 'hooks/use-user-data';
import _ from 'lodash';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { Assessment } from 'types/analysis-result.types';
import { OptionType } from '../../ui/base-components/pai-auto-complete';
import PaiButton from '../../ui/base-components/pai-button';
import PaiDotLoader from '../../ui/base-components/pai-dot-loader';
import PaiSearchSelector from '../../ui/base-components/pai-search-selector';
import { Occupation } from '../text-generation.types';
import PersonalityType from './personality-type';

interface Props {
  occupationSearch: string;
  occupations: Occupation[];
  occupationsLoading: boolean;
  assessment: Assessment | undefined;
  profileLoading: boolean;

  handleOccupationSearchChange(event: React.ChangeEvent<HTMLInputElement>): void;

  occupationSearchKeyUp(): void;

  occupationSearchKeyDown(): void;

  handleOccupationClick(occupationId: string): () => void;
}

const GlobalProfileSelection: React.FC<Props> = ({
  occupationSearch,
  occupations,
  occupationsLoading,
  assessment,
  profileLoading,
  handleOccupationSearchChange,
  occupationSearchKeyUp,
  occupationSearchKeyDown,
  handleOccupationClick,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const { firstName } = useUserData();
  const occupationOptions: OptionType[] = useMemo(
    () => occupations.map((occupation) => ({ label: occupation.occupation, value: occupation.id })),
    [occupations]
  );

  const mailtoAddress = 'simon.tschuertz@secret-ai-agent.de; info@secret-ai-agent.de';

  const openMailMessenger = () => {
    window.location.href = `mailto:${mailtoAddress}?subject=${t('requestOccupationMailSubject', { profileName: occupationSearch })}&body=${t(
      'requestOccupationMailBody',
      { profileName: occupationSearch, userName: firstName }
    )}`;
  };

  return (
    <div className={classes.profileSelection}>
      <Stack className={classes.content} gap={!_.isNil(assessment) ? 1 : 4}>
        <Stack gap={2}>
          <div>
            <Typography className={classes.shortInstruction}>{t('selectGlobalProfile')}</Typography>
            <Typography className={classes.longInstruction}>{t('selectProfileInstructions')}</Typography>
          </div>
          <PaiSearchSelector
            value={occupationSearch}
            options={occupationOptions}
            placeholder={t('enterProfession')}
            listItemActionText={t('select')}
            disableBorder={true}
            loading={occupationsLoading}
            onChange={handleOccupationSearchChange}
            onKeyUp={occupationSearchKeyUp}
            onKeyDown={occupationSearchKeyDown}
            onMenuItemClick={handleOccupationClick}
          />
          {profileLoading ? <PaiDotLoader /> : null}
        </Stack>
        {!_.isNil(assessment) ? (
          <PersonalityType assessment={assessment} />
        ) : (
          <Stack gap={2}>
            <div>
              <Typography className={classes.shortInstruction}>{t('nothingSuitable')}</Typography>
              <Typography className={classes.longInstruction}>{t('timeUntilProfileCreated')}</Typography>
            </div>
            <PaiButton text={t('requestGlobalProfile')} action={openMailMessenger} fullWidth={true} />
          </Stack>
        )}
      </Stack>
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  profileSelection: {
    height: 'calc(100% - 39px - 16px - 20px)',
    border: `2px solid ${theme.palette.backgrounds.main}`,
    padding: '12px 16px',
  },
  content: {
    height: '100%',
    padding: '16px',
    overflow: 'auto',
  },
  shortInstruction: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 'bold',
    textDecoration: 'underline',
    marginBottom: '8px',
  },
  longInstruction: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
    padding: '0 4px',
  },
}));

export default GlobalProfileSelection;
